/* eslint-disable prefer-arrow-callback */
import React, { useEffect, useRef } from "react";

import { productAnalytics, useNavigate, useParams } from "utils";

import { ScenarioOnboardingProvider, useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { Step1BaselinePage } from "pages/app/scenario-onboarding/Step1BaselinePage";
import { Step2BaselineForecastPage } from "pages/app/scenario-onboarding/Step2BaselineForecastPage";
import { Step3ImprovedScenarioPage } from "pages/app/scenario-onboarding/Step3ImprovedScenarioPage";
import { useOnboardingDraftRestore } from "pages/app/scenario-onboarding/hooks/useOnboardingDraftRestore";
import { useSoilSightOnboardingAccess } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingAccess";

const VALID_STEPS = new Set(["step-1", "step-2", "step-3"]);

const OnboardingStepRouter = ({ step, hasRestorableDraft }: { step: string; hasRestorableDraft: boolean }) => {
  const navigate = useNavigate();
  // The restore runs here (not per step page) so a refresh on ANY step rebuilds the whole
  // flow — baseline, change scenario, outcomes and highlights — from the server.
  useOnboardingDraftRestore();
  const { outcomes, calcStatus, scenarioRefs, changeCropYears, restoreStatus } = useScenarioOnboarding();

  // While a restorable draft is still being rebuilt, hold the guards — otherwise a refresh
  // on steps 2 or 3 would bounce to step 1 before the restore has a chance to finish.
  const isRestorePending = hasRestorableDraft && (restoreStatus === "idle" || restoreStatus === "restoring");

  const needsCalcResults = step !== "step-1";
  const needsChangeScenario = step === "step-3";
  // Steps 2 and 3 stay reachable while calculating and after a failed calculation (they show "—"),
  // provided the change scenario was persisted. A failure before that leaves no refs and bounces.
  const hasDegradedResults = calcStatus === "calculating" || (calcStatus === "error" && Boolean(scenarioRefs.changeScenarioId));
  const guardBlocked =
    !isRestorePending &&
    ((needsCalcResults && !outcomes && !hasDegradedResults) || (needsChangeScenario && (!scenarioRefs.changeScenarioId || changeCropYears.length === 0)));

  useEffect(() => {
    if (guardBlocked) {
      navigate("/scenarios-onboarding/step-1", { replace: true });
    }
  }, [guardBlocked, navigate]);

  const isHidden = guardBlocked || (isRestorePending && step !== "step-1");

  // We report each step once. A later restore status change re-renders the same step.
  const trackedStepRef = useRef<string | null>(null);
  useEffect(
    function trackStepView() {
      if (isHidden || trackedStepRef.current === step) return;
      trackedStepRef.current = step;
      productAnalytics.track("soilsights_onboarding:step_view", { step_index: Number(step.replace("step-", "")), is_draft_restored: restoreStatus === "done" });
    },
    [isHidden, step, restoreStatus]
  );

  if (isHidden) {
    return null;
  }

  switch (step) {
    case "step-2":
      return <Step2BaselineForecastPage />;
    case "step-3":
      return <Step3ImprovedScenarioPage />;
    default:
      return <Step1BaselinePage />;
  }
};

export const ScenarioOnboardingWrapper = () => {
  const navigate = useNavigate();
  const { step } = useParams<{ step: string }>();
  const access = useSoilSightOnboardingAccess();

  // The access loading gate applies to ENTRY only. Mid-session, the flow's own first save
  // creates the project, which starts the (previously disabled) scenario-list query and
  // flips access.isLoading back to true for a moment — unmounting the provider here would
  // wipe the rows the user is editing and remount the wizard ("the page reloaded").
  const hasEnteredRef = useRef(false);
  if (!access.isLoading) {
    hasEnteredRef.current = true;
  }
  const isEntryLoading = access.isLoading && !hasEnteredRef.current;

  const stepIsValid = Boolean(step && VALID_STEPS.has(step));
  // access.isAllowed covers both onboarded-with-scenarios and the unverified-error fail-safe
  // (see useSoilSightOnboardingAccess) — safe mid-session too, since the flow's first save flips the
  // status to "in-progress", so isOnboarded stays false until the final step genuinely completes.
  const redirectToScenarios = !isEntryLoading && access.isAllowed;

  useEffect(() => {
    if (isEntryLoading) return;
    if (redirectToScenarios) {
      navigate("/scenarios", { replace: true });
      return;
    }
    if (!stepIsValid) {
      navigate("/scenarios-onboarding/step-1", { replace: true });
    }
  }, [isEntryLoading, redirectToScenarios, stepIsValid, navigate]);

  if (isEntryLoading || redirectToScenarios || !stepIsValid || !step) {
    return null;
  }

  return (
    <ScenarioOnboardingProvider>
      <OnboardingStepRouter step={step} hasRestorableDraft={access.hasDraftForActiveFarm && access.hasProject} />
    </ScenarioOnboardingProvider>
  );
};
